import { useEffect, useState } from 'react'
import { api, imHintergrund } from './api'
import { fachlicherSatz } from './Fehlersatz'
import { ANFANG, kontobezeichnung, t } from './sprache'
import type { Sprachschluessel } from './sprache'
import { Auswahlfeld, ausStammdatum } from './Auswahlfeld'
import { gliederungsfolge } from './Kfreigenschaften'
import { Periodenfeld } from './Periodenfeld'
import { useDarf } from './Rechte'
import { Spaltenmassleiste, useSpaltenmasse } from './Spaltenbreiten'
import { Blattabschnitt, periodenwort } from './Stammdaten'
import type { Abschluss, Listeneintrag } from './typen'

/** Die Stände eines Abschlusses, die ihren Stand einfrieren (R512). */
const EINGEFROREN = new Set(['freigegeben', 'geschlossen', 'testiert'])

/**
 * Die Zuordnungen, die **heute oder später** unter dieser Position stehen —
 * gemessen an der Periode des Berichts (R859).
 *
 * Was vorher endete, steht nicht mehr hier: Wer in der Bilanz Dezember 2026
 * fragt, welche Konten unter einer Position stehen, meint nicht das Konto, das
 * 2024 woanders hinwanderte.
 */
export function kontenDerPosition(zuordnungen: Listeneintrag[], position: string, periode?: number): Listeneintrag[] {
  return zuordnungen.filter((z) => {
    if (z.werte['position'] !== position) {
      return false
    }
    const bis = Number(z.werte['gueltig_bis'] || 0)
    return !bis || (periode !== undefined && bis >= periode)
  })
}

/**
 * Welche freigegebenen Abschlüsse ein **„immer"** erreicht (R860) — jeder mit
 * eingefrorenem Stand. Sie bleiben, wie sie freigegeben wurden; das Fenster
 * sagt es vor dem Speichern.
 */
export function eingefrorene(abschluesse: Abschluss[]): number[] {
  return [...new Set(abschluesse.filter((a) => EINGEFROREN.has(a.status)).map((a) => a.periode))].sort()
}

/**
 * **Welche Konten hier stehen** — im Eigenschaften-Fenster einer Position
 * (R859, R860, 7.3 Abschnitt 20).
 *
 * Je Konto die Gültigkeit und zwei Handgriffe: auf eine andere Position
 * umhängen, oder von dieser nehmen. Beides gilt **ab einer Periode** —
 * vorgeschlagen ist die des Berichts — **oder immer**; die Wahl steht einmal
 * über der Tabelle und gilt für jede Zeile darin.
 */
export function Positionskonten({
  position,
  periode,
  aufAenderung,
}: {
  position: string
  periode?: number
  aufAenderung?: () => void
}) {
  const [zuordnungen, setZuordnungen] = useState<Listeneintrag[] | null>(null)
  const [positionen, setPositionen] = useState<Listeneintrag[]>([])
  const [gliederung, setGliederung] = useState<Listeneintrag[]>([])
  const [abschluesse, setAbschluesse] = useState<Abschluss[]>([])
  const [immer, setImmer] = useState(false)
  const [ab, setAb] = useState<number | null>(periode ?? null)
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)
  const { masse, rahmen } = useSpaltenmasse<HTMLTableElement>('eigenschaften:positionskonten')
  // Was der Angemeldete nicht darf, ist nicht da (8.1 Abschnitt 7, R1180).
  const darf = useDarf()
  const darfUmhaengen = darf('kontoUmhaengen')
  const darfAushaengen = darf('kontoAushaengen')

  async function lies() {
    setZuordnungen((await api.stammdatenliste('kontozuordnung')).eintraege)
  }

  useEffect(() => {
    let gilt = true
    void Promise.all([
      api.stammdatenliste('kontozuordnung'),
      api.stammdatenliste('position'),
      api.stammdatenliste('positionsgliederung'),
      // Eine Nachschlageliste, im Hintergrund (7.2 Abschnitt 30, R1007).
      imHintergrund(() => api.abschluesse()).catch(() => [] as Abschluss[]),
    ]).then(([z, p, g, a]) => {
      if (!gilt) {
        return
      }
      setZuordnungen(z.eintraege)
      setPositionen(p.eintraege)
      setGliederung(g.eintraege)
      setAbschluesse(a)
    })
    return () => {
      gilt = false
    }
  }, [position])

  if (!zuordnungen) {
    return null
  }

  // **Nur Positionen desselben Plans** stehen zur Wahl: Ein Konto der
  // HGB-Gliederung unter eine IFRS-Position zu hängen, hieße, es in einer
  // Gliederung erscheinen zu lassen, die die andere nie liest.
  const plan = positionen.find((p) => p.schluessel === position)?.werte['positionsplan']
  const rang = gliederungsfolge(gliederung)
  const stelle = (s: string) => rang.get(s) ?? rang.size
  const ziele = positionen
    .filter((p) => p.werte['positionsplan'] === plan)
    .sort((a, b) => stelle(a.schluessel) - stelle(b.schluessel))
    .map(ausStammdatum)

  // Das Konto heißt, wie der Namensgeber es nennt (R1660).
  const kontoname = kontobezeichnung
  const eigene = kontenDerPosition(zuordnungen, position, periode).sort((a, b) =>
    kontoname(a.werte['konto'] ?? '').localeCompare(kontoname(b.werte['konto'] ?? ''), 'de', { numeric: true }),
  )
  const gefroren = eingefrorene(abschluesse)

  async function schreibe(tun: () => Promise<unknown>) {
    if (!immer && ab === null) {
      setMeldung(t('positionskonten.ohneperiode'))
      return
    }
    setLaeuft(true)
    setMeldung(null)
    try {
      await tun()
      aufAenderung?.()
      await lies()
    } catch (fehler) {
      // Nur die fachliche Absage steht hier; eine Störung meldet das
      // Störungsfenster (7.2 Abschnitt 30, R1003/R1004).
      setMeldung(fachlicherSatz(fehler))
    } finally {
      setLaeuft(false)
    }
  }

  const kopf = (feld: string) => <th>{t(`stammdaten.feld.${feld}` as Sprachschluessel)}</th>

  return (
    <Blattabschnitt kennung="eigenschaften.positionskonten" titel={t('positionskonten.titel')}>
      {/* **Die Wahl steht einmal über der Tabelle** (R860): ab einer Periode —
          vorgeschlagen die des Berichts — oder immer. */}
      {/* Die Wahl gilt nur für die Handgriffe darunter — ohne sie ist sie nicht da (R1180). */}
      {(darfUmhaengen || darfAushaengen) && (
      <fieldset className="wirkungswahl">
        <legend>{t('positionskonten.wirkung')}</legend>
        {/* Das Wort „ab" trägt das Periodenfeld — ein zweites daneben am
            Knopf läse sich „ab · ab Periode". **Knopf und Feld stehen in
            einer Klammer**, damit sie beim Umbrechen nicht auseinanderfallen
            (Fund vom 2026-10-02: der Knopf stand allein mitten im Fenster). */}
        <span className="wirkungab">
          <input
            type="radio"
            name="positionskonten-wirkung"
            aria-label={t('positionskonten.ab')}
            checked={!immer}
            onChange={() => setImmer(false)}
          />
          <Periodenfeld wert={ab} aufWert={setAb} gesperrt={immer} beschriftung={t('positionskonten.ab')} klasse="" />
        </span>
        <label>
          <input type="radio" name="positionskonten-wirkung" checked={immer} onChange={() => setImmer(true)} />
          {t('positionskonten.immer')}
        </label>
      </fieldset>
      )}

      {/* **„Immer" nennt die freigegebenen Abschlüsse, bevor gespeichert wird**
          (R860) — sie behalten ihren eingefrorenen Stand (R512). */}
      {immer && gefroren.length > 0 && (
        <p className="hinweis">
          {t('positionskonten.freigegeben', { abschluesse: gefroren.map(periodenwort).join(', ') })}
        </p>
      )}
      {meldung && <p className="hinweis">{meldung}</p>}

      <div className="tabellenrahmen">
        <table ref={rahmen} className="positionskonten ziehbaretabelle">
          <Spaltenmassleiste masse={masse} />
          <thead>
            <tr>
              {kopf('konto')}
              {kopf('gueltig_von')}
              {kopf('gueltig_bis')}
              {/* Ohne Umhängen nennte die Spalte in jeder Zeile dieselbe Position (R1180).
                  Sie ist ein Handgriff und heißt so (R1460). */}
              {darfUmhaengen && <th>{t('positionskonten.umhaengen')}</th>}
              {darfAushaengen && <th aria-label={t('positionskonten.entfernen')} />}
            </tr>
          </thead>
          <tbody>
            {eigene.map((z) => {
              const konto = z.werte['konto'] ?? ''
              return (
                <tr key={z.schluessel}>
                  <td>{kontoname(konto)}</td>
                  <td>
                    {/* Der Anfang der Zeitachse ist kein Januar 1900, sondern „schon immer". */}
                    {Number(z.werte['gueltig_von'] || 0) <= ANFANG
                      ? t('positionskonten.vonanfang')
                      : periodenwort(z.werte['gueltig_von'])}
                  </td>
                  <td>{z.werte['gueltig_bis'] ? periodenwort(z.werte['gueltig_bis']) : '—'}</td>
                  {darfUmhaengen && (
                  <td>
                    {/* Leer, und ohne die eigene Position (R1460): Bis zum
                        2026-10-09 stand hier zwölfmal derselbe Name. */}
                    <Auswahlfeld
                      wert=""
                      leer="—"
                      eintraege={ziele.filter((z) => z.wert !== position)}
                      abgeschaltet={laeuft}
                      beschriftung={t('stammdaten.feld.position')}
                      aufWahl={(neu) => {
                        if (neu && neu !== position) {
                          void schreibe(() => api.kontoUmhaengen(konto, neu, ab ?? periode ?? 0, false, immer))
                        }
                      }}
                    />
                  </td>
                  )}
                  {darfAushaengen && (
                  <td>
                    <button
                      type="button"
                      className="schmal"
                      disabled={laeuft}
                      onClick={() => void schreibe(() => api.kontoAushaengen(konto, position, ab ?? periode ?? 0, immer))}
                    >
                      {t('positionskonten.entfernen')}
                    </button>
                  </td>
                  )}
                </tr>
              )
            })}
            {eigene.length === 0 && (
              <tr>
                <td colSpan={3 + Number(darfUmhaengen) + Number(darfAushaengen)} className="hinweis">
                  {t('positionskonten.leer')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Blattabschnitt>
  )
}
